import { ConceptStatusValidity, ConceptValueDataType, ValidityDeterminate } from '@fusion-advantage/backcompat'
import { err, ok, Result } from 'neverthrow'
import { isConceptStatus } from '../../../concept/ConceptTypeGuards'
import { getConceptStatusValue } from '../../../concept/getConceptStatus'
import { evaluatorErrorWithNode } from '../../scriptevaluator/EvaluatorError'
import { BaseRunContext } from '../../scriptevaluator/RunContext'
import { CalcRuntimeError } from '../../scriptevaluator/RuntimeTypes'
import { functionNodeTypes, HelperFunction } from '../FunctionTypes'

export const DirValiditySkip = {
	REQUIRED: 'REQUIRED',
	RANGE: 'RANGE',
} as const

export interface DirectoryValidityOptions {
	skipRequired?: boolean
	skipRange?: boolean
}

// The validity implied by a concept's Directory attributes, checked in order:
// required, date (month needs a year), range/normal range, numeric data type.
// Shared by formrender's default validation calc and the DIR_VALIDITY function,
// so a script calc that replaces the default calc can keep the Directory checks.
export const directoryValidity = (
	ctx: BaseRunContext,
	cid: string,
	opts: DirectoryValidityOptions = {},
): Result<ConceptStatusValidity, CalcRuntimeError> => {
	const concept = ctx.getConcept(cid)
	if(!concept) {
		return err({
			area: 'directoryValidity',
			message: `Expected concept for '${cid}' but null`,
		})
	}
	if(!opts.skipRequired && concept.isRequired && !ctx.hasAnyValue(cid)) {
		return ok({ determinate: ValidityDeterminate.Required })
	}
	if(concept.valueDataType === ConceptValueDataType.Date) {
		const [month, year] = dateMonthYear(ctx, cid)
		if(month && !year) {
			return ok({ determinate: ValidityDeterminate.Required, message: 'Year is required when month is provided' })
		}
	}
	if(!opts.skipRange && concept.valueRange) {
		const status = ctx.getStatus(cid)
		if(isConceptStatus(status) && status.value && status.value !== '') {
			const value = Number(status.value)
			const range = concept.valueRange
			if(isNaN(value)) {
				return ok({ determinate: ValidityDeterminate.Invalid })
			}
			if(
				(range.rangeLow !== null && value < range.rangeLow) ||
				(range.rangeHigh !== null && value > range.rangeHigh)
			) {
				return ok({ determinate: ValidityDeterminate.OutOfRange })
			}
			if(
				(range.normalLow !== null && value < range.normalLow) ||
				(range.normalHigh !== null && value > range.normalHigh)
			) {
				return ok({ determinate: ValidityDeterminate.Abnormal })
			}
		}
	}
	if(isNumericDataType(concept.valueDataType) && ctx.hasAnyValue(cid)) {
		if(!ctx.hasValidValueForDataType(cid, concept.valueDataType!)) {
			return ok({ determinate: ValidityDeterminate.Invalid })
		}
	}
	return ok({ determinate: ValidityDeterminate.Valid })
}

const isNumericDataType = (valueDataType: ConceptValueDataType | null) => {
	return valueDataType === ConceptValueDataType.Decimal ||
		valueDataType === ConceptValueDataType.Integer
}

// a date value is stored as YYYYMMDD, with '*' for an unselected part
const dateMonthYear = (ctx: BaseRunContext, cid: string): [string, string] => {
	const value = getConceptStatusValue(ctx.getStatus(cid))
	if(!value) return ['', '']
	const dateString = String(value)
	const year = dateString.slice(0, 4)
	const month = dateString.slice(4, 6)
	return [month.includes('*') ? '' : month, year.includes('*') ? '' : year]
}

const determinateKeyword: Record<ValidityDeterminate, string> = {
	[ValidityDeterminate.Valid]: 'VALID',
	[ValidityDeterminate.Invalid]: 'INVALID',
	[ValidityDeterminate.Abnormal]: 'ABNORMAL',
	[ValidityDeterminate.OutOfRange]: 'OUT_OF_RANGE',
	[ValidityDeterminate.Required]: 'REQUIRED',
}

// DIR_VALIDITY(cid) or DIR_VALIDITY(cid, ["REQUIRED", "RANGE"])
// Returns the Directory validity as its RETURN keyword string ("VALID",
// "OUT_OF_RANGE", ...). The optional list names checks to skip.
export const DirValidityFunction: HelperFunction = {
	tokenName: 'DIR_VALIDITY',
	nodeType: functionNodeTypes.DirValidity,
	evaluate(node, evaluateExpr, context) {
		const args = node.arguments
		if (args.length < 1 || args.length > 2) {
			return err(evaluatorErrorWithNode('DIR_VALIDITY function expects one or two arguments', node))
		}
		const conceptResult = evaluateExpr(args[0])
		if (conceptResult.isErr()) return err(conceptResult.error)
		if (typeof conceptResult.value !== 'string') {
			return err(evaluatorErrorWithNode('DIR_VALIDITY function first argument must evaluate to a string', node))
		}
		let skips: unknown[] = []
		if (args[1]) {
			const skipsResult = evaluateExpr(args[1])
			if (skipsResult.isErr()) return err(skipsResult.error)
			if (!Array.isArray(skipsResult.value)) {
				return err(evaluatorErrorWithNode('DIR_VALIDITY second argument must be an array', node))
			}
			skips = skipsResult.value
			const unknownSkip = skips.find(s => s !== DirValiditySkip.REQUIRED && s !== DirValiditySkip.RANGE)
			if (unknownSkip !== undefined) {
				return err(evaluatorErrorWithNode(`DIR_VALIDITY cannot skip '${unknownSkip}' (expected "REQUIRED" or "RANGE")`, node))
			}
		}
		const rValidity = directoryValidity(context, conceptResult.value, {
			skipRequired: skips.includes(DirValiditySkip.REQUIRED),
			skipRange: skips.includes(DirValiditySkip.RANGE),
		})
		if (rValidity.isErr()) {
			return err(evaluatorErrorWithNode(rValidity.error.message, node))
		}
		return ok(determinateKeyword[rValidity.value.determinate])
	}
}
